import { useCallback } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { mapApiError } from "@njbsoft/i18n";

const SUCCESS_DURATION = 4000;
const ERROR_DURATION = 6000;

/** An inline action button (e.g. "Undo") rendered inside the toast. */
interface ToastAction {
  labelKey: string;
  onClick: () => void;
}

interface SuccessOptions {
  /** Interpolation values for the message key (e.g. `{ title }`, `{ count }`). */
  values?: Record<string, string | number>;
  /** Optional action button rendered inside the toast. */
  action?: ToastAction;
}

interface ErrorOptions {
  /** Interpolation values for the message key — a failure the server named. */
  values?: Record<string, string | number>;
}

interface ToastActions {
  success: (i18nKey: string, opts?: SuccessOptions) => void;
  /**
   * An outcome that is neither clean nor a failure — work that landed, short of
   * what was asked. Reporting one of those as an error trains the officer to
   * distrust red, and as a success hides that something is still owed.
   */
  warning: (i18nKey: string, opts?: SuccessOptions) => void;
  error: (i18nKey: string, opts?: ErrorOptions) => void;
  apiError: (code: string | undefined) => void;
}

export function useToast(): ToastActions {
  const { t } = useTranslation();

  const actionOption = useCallback(
    (action: ToastAction | undefined) =>
      action ? { action: { label: t(action.labelKey), onClick: action.onClick } } : {},
    [t],
  );

  const success = useCallback(
    (i18nKey: string, opts?: SuccessOptions) => {
      toast.success(t(i18nKey, opts?.values), {
        duration: SUCCESS_DURATION,
        ...actionOption(opts?.action),
      });
    },
    [t, actionOption],
  );

  const warning = useCallback(
    (i18nKey: string, opts?: SuccessOptions) => {
      toast.warning(t(i18nKey, opts?.values), {
        duration: SUCCESS_DURATION,
        ...actionOption(opts?.action),
      });
    },
    [t, actionOption],
  );

  const error = useCallback(
    (i18nKey: string, opts?: ErrorOptions) => {
      toast.error(t(i18nKey, opts?.values), { duration: ERROR_DURATION });
    },
    [t],
  );

  const apiError = useCallback((code: string | undefined) => {
    toast.error(mapApiError(code), { duration: ERROR_DURATION });
  }, []);

  return { success, warning, error, apiError };
}
